import { AlertTriangle, MessageSquareText } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { getThreadDisplayName, getThreadInitials } from "@/lib/messages/messages-helpers";
import { deriveClinicShortName, numberLabelForThread } from "@/lib/messages/merged-threads";
import { splitHighlightParts } from "@/lib/messages/search-highlight";
import { tenantColorVars } from "@/lib/messages/merged-thread-colors";
import { appDayKey, withAppTimeZone } from "@/lib/timezone";
import { cn } from "@/lib/utils";

/** Snippet text with every occurrence of the term marked. */
function HighlightedSnippet({ text, term }) {
  const parts = splitHighlightParts(text, term);
  return (
    <>
      {parts.map((part, index) =>
        part.match ? (
          // eslint-disable-next-line react/no-array-index-key -- parts are positional by nature
          <mark key={index} className="chat-search-mark">
            {part.text}
          </mark>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{part.text}</span>
        ),
      )}
    </>
  );
}

function formatHitTime(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const time = date.toLocaleTimeString([], withAppTimeZone({ hour: "2-digit", minute: "2-digit" }));
  if (appDayKey(date) === appDayKey(new Date())) return time;
  return `${date.toLocaleDateString([], withAppTimeZone({ day: "numeric", month: "short" }))} · ${time}`;
}

/**
 * The result panel that drops over the top of the conversation while searching.
 *
 * It overlays (absolute) rather than sitting in the flex column on purpose: the message pane's
 * scroll geometry — stick-to-bottom, prepend anchoring, the late-growth ResizeObserver — is all
 * measured against that viewport, and shrinking it would fight every one of them.
 *
 * Rows carry their own threadId, which is what a click reveals. That matters in the merged
 * WhatsApp view, where consecutive hits routinely belong to different per-clinic threads.
 */
export function ChatSearchResults({
  term,
  results,
  threadsById,
  activeIndex,
  loading,
  loadingMore,
  hasMore,
  error,
  truncated,
  allChats,
  mergedActive,
  getSegmentMeta,
  getSenderLabel,
  whatsappNumbers,
  onSelect,
  onLoadMore,
}) {
  const showEmpty = !loading && !error && results.length === 0;

  return (
    // The height cap MUST sit on the ScrollArea's viewport, not on this wrapper: the Radix Root is
    // auto-height, so a max-height above it clips the list (overflow-hidden) without ever making it
    // scrollable — the exact trap scroll-area.jsx warns about. `cqh` makes the cap half the
    // CONVERSATION PANE (which declares itself a size container) rather than half the window: a
    // vh-based cap swallowed ~80% of a short pane and hid the message a click had just revealed.
    // The wrapper stays auto-height so a two-result panel is still small.
    <div className="absolute inset-x-0 top-0 z-20 border-b border-border bg-background/98 shadow-lg backdrop-blur-sm">
      <ScrollArea viewportClassName="max-h-[50cqh]">
        {error ? (
          <p className="flex items-center gap-2 px-4 py-3 text-xs text-rose-600 dark:text-rose-400">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}

        {showEmpty ? (
          <p className="flex items-center gap-2 px-4 py-4 text-xs text-muted-foreground">
            <MessageSquareText className="h-3.5 w-3.5 shrink-0" aria-hidden />
            No messages match “{term}”
            {allChats ? " in any conversation." : " in this conversation."}
          </p>
        ) : null}

        <ul className="divide-y divide-border/50">
          {results.map((row, index) => {
            const thread = threadsById.get(row.threadId);
            const botId = String(thread?.whatsappBotId || "").trim();
            // A clinic chip only means something when several conversations are on screen at once.
            const showClinic = Boolean(botId) && (mergedActive || allChats);
            const clinicLabel = showClinic
              ? deriveClinicShortName(
                  getSegmentMeta?.(botId)?.label || numberLabelForThread(thread, whatsappNumbers),
                )
              : "";
            const senderLabel = getSenderLabel?.(row) || "";
            const threadTitle = thread ? getThreadDisplayName(thread).label : "";
            const isActive = index === activeIndex;

            return (
              <li key={`${row.threadId}:${row.messageId}`}>
                <button
                  type="button"
                  onClick={() => onSelect(index)}
                  aria-current={isActive ? "true" : undefined}
                  className={cn(
                    "flex w-full items-start gap-2.5 px-4 py-2 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                    isActive ? "bg-amber-50 dark:bg-amber-500/10" : "hover:bg-muted/60",
                  )}
                >
                  {/* Only an all-chats hit needs to say WHICH conversation it came from. */}
                  {allChats ? (
                    <Avatar className="mt-0.5 h-7 w-7 shrink-0">
                      <AvatarImage src={thread?.avatarUrl} />
                      <AvatarFallback className="text-[9px] font-semibold">
                        {getThreadInitials(threadTitle)}
                      </AvatarFallback>
                    </Avatar>
                  ) : null}

                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-none">
                      {allChats && threadTitle ? (
                        <span className="truncate font-semibold text-foreground">{threadTitle}</span>
                      ) : null}
                      {senderLabel ? (
                        <span className="truncate font-medium text-muted-foreground">{senderLabel}</span>
                      ) : null}
                      {showClinic && clinicLabel ? (
                        <span
                          className="inline-flex shrink-0 items-center gap-1 text-muted-foreground"
                          style={tenantColorVars(botId)}
                        >
                          <span
                            className="h-2 w-2 shrink-0 rounded-full bg-(--seg-solid)"
                            aria-hidden
                          />
                          {clinicLabel}
                        </span>
                      ) : null}
                      <span className="ml-auto shrink-0 tabular-nums text-muted-foreground">
                        {formatHitTime(row.createdAt)}
                      </span>
                    </span>
                    <span className="line-clamp-2 text-xs leading-snug text-foreground/90">
                      <HighlightedSnippet text={row.snippet} term={term} />
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>

        {hasMore ? (
          <div className="flex justify-center border-t border-border/50 px-4 py-2">
            <button
              type="button"
              disabled={loadingMore}
              onClick={onLoadMore}
              className="rounded-md px-3 py-1 text-xs font-medium text-indigo-600 transition hover:bg-muted disabled:opacity-50 dark:text-indigo-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {loadingMore ? "Loading…" : "Load more results"}
            </button>
          </div>
        ) : null}

        {/* The server clipped how many conversations it reached, so recall is partial — say so
            rather than letting the list imply it searched everything. */}
        {truncated ? (
          <p className="border-t border-border/50 px-4 py-1.5 text-[11px] text-muted-foreground">
            Only your most recently active conversations were searched. Narrow the search with a
            date filter or open the conversation and search inside it.
          </p>
        ) : null}
      </ScrollArea>
    </div>
  );
}
